import { Dialog as RadixDialog } from "radix-ui";
import { clsx } from "clsx";
import { X } from "lucide-react";
import { twMerge } from "tailwind-merge";

import { Button } from "../Button/Button";

export type DialogProps = Pick<
  RadixDialog.DialogProps,
  "open" | "onOpenChange"
> & {
  title: string;
  rootProps?: Omit<Partial<RadixDialog.DialogProps>, "open" | "onOpenChange">;
  contentProps?: Partial<RadixDialog.DialogContentProps>;
} & (
    | {
        children: React.ReactNode;
        description?: string | JSX.Element;
      }
    | {
        children?: React.ReactNode;
        description: string | JSX.Element;
      }
  );

export function Dialog({
  title,
  description,
  open,
  onOpenChange,
  children,
  rootProps,
  contentProps,
}: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange} {...rootProps}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut z-overlay fixed inset-0 flex items-center justify-center bg-black/50 p-12">
          <RadixDialog.Content
            onPointerDownOutside={() => onOpenChange?.(false)}
            onEscapeKeyDown={() => onOpenChange?.(false)}
            onOpenAutoFocus={(e) => e.preventDefault()}
            {...contentProps}
            className={twMerge(
              clsx(
                "data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut flex max-h-full w-125 flex-col rounded-lg bg-white shadow-[0px_2px_12px_rgba(0,0,0,0.2)] dark:bg-gray-100",
                contentProps?.className,
              ),
            )}
          >
            <div className="flex flex-none items-center justify-between pt-6 pr-6 pb-3 pl-6">
              <RadixDialog.Title className="text-[20px] font-semibold">
                {title}
              </RadixDialog.Title>
              {onOpenChange && (
                <RadixDialog.Close asChild>
                  <Button
                    aria-label="Close dialog"
                    variant="icon-ghost"
                    size="md"
                    onClick={() => onOpenChange?.(false)}
                  >
                    <X aria-hidden />
                  </Button>
                </RadixDialog.Close>
              )}
            </div>

            <div className="overflow-auto px-6 text-sm">
              {description && (
                <RadixDialog.Description asChild>
                  <div className="py-6">{description}</div>
                </RadixDialog.Description>
              )}
              {!description && <RadixDialog.Description className="sr-only" />}
              {children}
            </div>
          </RadixDialog.Content>
        </RadixDialog.Overlay>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
interface DialogActionsWrapperProps {
  children: React.ReactNode;
  className?: string;
}
export function DialogActionsWrapper({
  children,
  className,
}: DialogActionsWrapperProps) {
  return (
    <div
      className={twMerge(
        "flex items-center justify-end gap-2 pt-3 pb-6",
        className,
      )}
    >
      {children}
    </div>
  );
}
